import { useEffect, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

const NS = 'chat';

/**
 * A `t` for rendering a turn's research trail in `language`, or the UI's own
 * `t` when no language is pinned — see `trailLanguageFor` for when one is.
 *
 * The web app loads one catalog at a time (`load: 'currentOnly'` in `i18n.ts`),
 * so a trail pinned to the answer's language may name a catalog that is not in
 * memory yet. It is fetched on demand and the UI's `t` stands in until it
 * lands, which shows the backend's English fallback text for a moment rather
 * than a blank line or a raw key. The mobile client bundles every catalog and
 * so skips the load.
 */
export function useTrailTranslation(language?: string): TFunction<'chat'> {
  const { t, i18n } = useTranslation(NS);
  const target = language && language !== i18n.language ? language : undefined;
  const [loaded, setLoaded] = useState(() => !!target && i18n.hasResourceBundle(target, NS));

  useEffect(() => {
    if (!target) return undefined;
    if (i18n.hasResourceBundle(target, NS)) {
      setLoaded(true);
      return undefined;
    }
    let cancelled = false;
    setLoaded(false);
    i18n
      .loadLanguages(target)
      .then(() => {
        if (!cancelled) setLoaded(true);
      })
      // A failed fetch leaves the UI's `t` in place, and with it the English
      // fallback text — the same line the trail showed before it had keys.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [target, i18n]);

  return target && loaded ? i18n.getFixedT(target, NS) : t;
}
